import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
  useWindowDimensions,
  type StyleProp,
  type TextInputProps,
  type ViewStyle,
} from "react-native";
import { useTranslation } from "react-i18next";
import { uiColors, uiMetrics } from "@toy/contracts/ui";
import { AdminApiError } from "./api";

export const colors = uiColors.light;
export const ui = StyleSheet.create({
  stack: { gap: 16 },
  row: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    borderRadius: uiMetrics.radius.md,
    padding: 20,
    gap: 16,
    minWidth: 0,
  },
  text: { fontSize: 14, lineHeight: 21, color: colors.text },
  muted: { fontSize: 12, lineHeight: 18, color: colors.muted },
  heading: {
    fontSize: 18,
    lineHeight: 25,
    fontWeight: "600",
    color: colors.text,
  },
  label: { fontSize: 13, lineHeight: 19, color: colors.muted },
  input: {
    minHeight: uiMetrics.control.touchHeight,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: uiMetrics.radius.sm,
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: colors.text,
    backgroundColor: colors.surface,
    fontSize: 14,
  },
  field: { gap: 6, minWidth: 150, flexGrow: 1, flexBasis: 200 },
  error: { color: colors.danger, fontSize: 14, lineHeight: 21 },
  button: {
    minHeight: uiMetrics.control.height,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: uiMetrics.radius.sm,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    alignSelf: "flex-start",
    justifyContent: "center",
  },
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.36)",
    padding: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  dialog: {
    maxWidth: 720,
    width: "100%",
    maxHeight: "90%",
    backgroundColor: colors.surface,
    borderRadius: uiMetrics.radius.lg,
    padding: 20,
    gap: 16,
  },
});

export function useResource<T>(loader: () => Promise<T>) {
  const generation = useRef(0);
  const [state, setState] = useState<{
    data?: T;
    error?: unknown;
    loading: boolean;
  }>({ loading: true });
  const reload = useCallback(async () => {
    const request = ++generation.current;
    setState({ loading: true });
    try {
      const data = await loader();
      if (request === generation.current) setState({ data, loading: false });
    } catch (error) {
      if (request === generation.current) setState({ error, loading: false });
    }
  }, [loader]);
  useEffect(() => {
    void reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);
  return { ...state, reload };
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: string;
  actions?: ReactNode;
}) {
  return (
    <View
      style={[
        ui.row,
        { justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
      ]}
    >
      <View style={{ gap: 6, flex: 1, minWidth: 200 }}>
        <Text
          accessibilityRole="header"
          style={{
            fontSize: 24,
            lineHeight: 32,
            letterSpacing: -0.4,
            fontWeight: "600",
            color: colors.text,
          }}
        >
          {title}
        </Text>
        <Text style={[ui.muted, { fontSize: 14, lineHeight: 21 }]}>{subtitle}</Text>
      </View>
      {actions}
    </View>
  );
}

export function Action({
  label,
  onPress,
  disabled = false,
  tone = "secondary",
  selected = false,
  style,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  tone?: "primary" | "secondary" | "danger" | "quiet";
  selected?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const { width } = useWindowDimensions();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled, selected }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        ui.button,
        {
          opacity: disabled ? 0.45 : pressed ? 0.7 : 1,
          minHeight: width < 768 ? uiMetrics.control.touchHeight : uiMetrics.control.height,
          backgroundColor:
            tone === "primary"
              ? colors.primary
              : tone === "danger"
                ? colors.danger
                : selected || pressed
                  ? colors.navigationSelected
                  : "transparent",
          borderColor:
            tone === "quiet"
              ? "transparent"
              : tone === "danger"
                ? colors.danger
                : tone === "primary"
                  ? colors.primary
                  : colors.border,
        },
        style,
      ]}
    >
      <Text
        style={[
          ui.text,
          {
            color:
              tone === "primary"
                ? colors.onPrimary
                : tone === "danger"
                  ? "#ffffff"
                  : colors.text,
            fontWeight: "500",
          },
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );
}

export function Field({
  label,
  value,
  onChange,
  ...props
}: { label: string; value: string; onChange: (value: string) => void } & Omit<
  TextInputProps,
  "value" | "onChange"
>) {
  return (
    <View style={ui.field}>
      <Text style={ui.label}>{label}</Text>
      <TextInput
        accessibilityLabel={label}
        value={value}
        onChangeText={onChange}
        placeholderTextColor={colors.muted}
        {...props}
        style={[
          ui.input,
          props.multiline && { minHeight: 90, textAlignVertical: "top" },
          props.style,
        ]}
      />
    </View>
  );
}

export function Toggle({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <View style={ui.row}>
      <Switch
        accessibilityLabel={label}
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        trackColor={{ true: colors.accent }}
      />
      <Text style={[ui.text, disabled && { color: colors.muted }]}>
        {label}
      </Text>
    </View>
  );
}

export function Choice({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <View style={ui.field}>
      <Text style={ui.label}>{label}</Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled, expanded: open }}
        disabled={disabled}
        onPress={() => setOpen(true)}
        style={ui.input}
      >
        <Text style={ui.text}>
          {options.find((option) => option.value === value)?.label ?? "—"}
        </Text>
      </Pressable>
      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <View style={ui.backdrop}>
          <View accessibilityViewIsModal style={ui.dialog}>
            <Text accessibilityRole="header" style={ui.heading}>
              {label}
            </Text>
            <ScrollView contentContainerStyle={{ gap: 4 }}>
              {options.map((option) => (
                <Pressable
                  key={option.value}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: option.value === value }}
                  onPress={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  style={[
                    ui.button,
                    {
                      width: "100%",
                      backgroundColor:
                        option.value === value
                          ? colors.navigationSelected
                          : colors.surface,
                    },
                  ]}
                >
                  <Text style={ui.text}>{option.label}</Text>
                </Pressable>
              ))}
            </ScrollView>
            <Action label={t("common.cancel")} onPress={() => setOpen(false)} />
          </View>
        </View>
      </Modal>
    </View>
  );
}

export function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal transparent animationType="fade" onRequestClose={onClose}>
      <View style={ui.backdrop}>
        <View accessibilityViewIsModal style={ui.dialog}>
          <Text accessibilityRole="header" style={ui.heading}>
            {title}
          </Text>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={ui.stack}
          >
            {children}
          </ScrollView>
          <Action label={t("common.cancel")} onPress={onClose} />
        </View>
      </View>
    </Modal>
  );
}

export interface TableColumn<T> {
  key: string;
  label: string;
  render: (item: T) => ReactNode;
  width?: number;
}
export function DataTable<T>({
  items,
  columns,
  itemKey,
}: {
  items: readonly T[];
  columns: readonly TableColumn<T>[];
  itemKey: (item: T) => string;
}) {
  const { t } = useTranslation();
  return (
    <ScrollView horizontal style={{ flexGrow: 0 }}>
      <View style={{ minWidth: "100%" }}>
        <View
          style={{
            flexDirection: "row",
            borderBottomWidth: 1,
            borderColor: colors.border,
          }}
        >
          {columns.map((column) => (
            <Text
              key={column.key}
              style={[ui.label, { width: column.width ?? 160, padding: 10 }]}
            >
              {column.label}
            </Text>
          ))}
        </View>
        {items.length === 0 && (
          <Text style={[ui.muted, { padding: 16 }]}>{t("common.noData")}</Text>
        )}
        {items.map((item) => (
          <View
            key={itemKey(item)}
            style={{
              flexDirection: "row",
              borderBottomWidth: 1,
              borderColor: colors.border,
            }}
          >
            {columns.map((column) => {
              const content = column.render(item);
              return (
                <View
                  key={column.key}
                  accessibilityLabel={column.label}
                  style={{
                    width: column.width ?? 160,
                    padding: 10,
                    justifyContent: "center",
                    gap: 6,
                  }}
                >
                  {typeof content === "string" ||
                  typeof content === "number" ? (
                    <Text
                      selectable
                      style={[
                        ui.text,
                        { fontVariant: ["tabular-nums"] },
                        typeof content === "number" && { textAlign: "right" },
                      ]}
                    >
                      {content}
                    </Text>
                  ) : (
                    content
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

export function ResourceState({
  loading,
  error,
  onRetry,
}: {
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (loading)
    return (
      <View
        accessibilityLiveRegion="polite"
        style={[ui.row, { paddingVertical: 12 }]}
      >
        <ActivityIndicator color={colors.accent} />
        <Text style={ui.muted}>{t("common.thinking")}</Text>
      </View>
    );
  if (!error) return null;
  return (
    <View accessibilityRole="alert" style={ui.row}>
      <Text style={ui.error}>
        {error instanceof AdminApiError && error.status === 409
          ? t("common.revisionConflict")
          : t("common.requestFailed")}
      </Text>
      <Action label={t("common.retry")} onPress={onRetry} />
    </View>
  );
}

export function StatusBadge({ value }: { value: string }) {
  const { t } = useTranslation();
  const color = [
    "healthy",
    "active",
    "available",
    "succeeded",
    "configured",
    "featured",
    "enabled",
  ].includes(value)
    ? "#166534"
    : ["warning", "degraded", "not_integrated", "unknown"].includes(value)
      ? "#92400e"
      : ["boolean", "limit"].includes(value)
        ? colors.muted
        : colors.danger;
  return (
    <Text style={{ fontSize: 12, color, alignSelf: "flex-start" }}>
      {t(`status.${value}`, { defaultValue: value.replaceAll("_", " ") })}
    </Text>
  );
}

export function formatDate(value: string | null): string {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}
export function RiskConfirmation({
  reason,
  onReasonChange,
  confirmed,
  onConfirmedChange,
  placeholder,
}: {
  reason: string;
  onReasonChange: (value: string) => void;
  confirmed: boolean;
  onConfirmedChange: (value: boolean) => void;
  placeholder: string;
}) {
  const { t } = useTranslation();
  return (
    <View style={ui.stack}>
      <Field
        label={t("common.reason")}
        value={reason}
        onChange={onReasonChange}
        placeholder={placeholder}
        multiline
      />
      <Toggle
        label={t("common.confirm")}
        value={confirmed}
        onChange={onConfirmedChange}
      />
    </View>
  );
}
